import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Separator } from '../../../components/ui/separator';
import { Badge } from '../../../components/ui/badge';
import { Plus, Trash2, ArrowLeft, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';

const labelClassName = 'text-sm font-medium text-foreground';
const fieldClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50';
const selectTriggerClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary/50';

export default function AddInvoice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { post, get } = useApi();
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [existingInvoices, setExistingInvoices] = useState([]);

  const [formData, setFormData] = useState({
    quote_id: '',
    account_id: '',
    invoice_number: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    gst_type: 'intra',
    cgst_rate: 9.0,
    cgst_amount: 0,
    sgst_rate: 9.0,
    sgst_amount: 0,
    igst_rate: null,
    igst_amount: null,
    total_tax: 0,
    subtotal: 0,
    total: 0,
    status: 'draft',
    notes: 'Payment due within 15 days via NEFT/UPI.',
    items: []
  });

  // Fetch quotes and existing invoices on component mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchQuotes(),
        fetchExistingInvoices()
      ]);
    };
    loadData();
  }, []);

  // Handle quoteId from URL parameters
  useEffect(() => {
    const quoteId = searchParams.get('quoteId');
    if (quoteId && quotes.length > 0) {
      // Find the quote with the matching ID
      const quote = quotes.find(q => q._id === quoteId);
      if (quote) {
        handleQuoteSelect(quoteId);
      }
    }
  }, [quotes, searchParams]);

  const fetchQuotes = async () => {
    try {
      const response = await get('/admin/quotes?status=approved');
      if (response?.success && Array.isArray(response?.data)) {
        setQuotes(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch quotes');
    }
  };

  const fetchExistingInvoices = async () => {
    try {
      const response = await get('/invoices');
      if (response?.success && Array.isArray(response?.data)) {
        setExistingInvoices(response.data);
      } else if (Array.isArray(response)) {
        setExistingInvoices(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setExistingInvoices(response.data);
      }
    } catch (error) {
      // Don't show error toast for this as it's not critical
    }
  };

  // Filter out quotes that already have invoices
  const getAvailableQuotes = () => {
    if (existingInvoices.length === 0) {
      return quotes; // If no invoices exist, return all quotes
    }

    // Get quote numbers that already have invoices
    const usedQuoteNumbers = existingInvoices
      .filter(invoice => {
        const normalizedStatus = (invoice.status || '').toLowerCase();
        return normalizedStatus !== 'cancelled' && normalizedStatus !== 'cancel';
      })
      .map(invoice => {
        // Check for quoteNumber field in the invoice
        const quoteNumber = invoice.quoteNumber;
        return quoteNumber;
      })
      .filter(Boolean);

    // Filter out quotes that already have invoices
    const availableQuotes = quotes.filter(quote => {
      const quoteNumber = quote.quoteNumber;
      const isUsed = usedQuoteNumbers.includes(quoteNumber);
      return !isUsed;
    });

    return availableQuotes;
  };

  const handleInputChange = (field, value) => {
    // Handle GST type changes with proper reset
    if (field === 'gst_type') {
      if (value === 'none') {
        setFormData(prev => ({
          ...prev,
          gst_type: value,
          cgst_rate: 0,
          sgst_rate: 0,
          igst_rate: 0,
          cgst_amount: 0,
          sgst_amount: 0,
          igst_amount: 0,
          total_tax: 0,
          total: prev.subtotal
        }));
      } else if (value === 'intra') {
        setFormData(prev => ({
          ...prev,
          gst_type: value,
          igst_rate: 0,
          igst_amount: 0
        }));
        setTimeout(() => recalculateTotals(), 0);
      } else if (value === 'inter') {
        setFormData(prev => ({
          ...prev,
          gst_type: value,
          cgst_rate: 0,
          sgst_rate: 0,
          cgst_amount: 0,
          sgst_amount: 0
        }));
        setTimeout(() => recalculateTotals(), 0);
      }
    } else if (field === 'cgst_rate' || field === 'sgst_rate' || field === 'igst_rate') {
      // Update the field first
      setFormData(prev => ({
        ...prev,
        [field]: parseFloat(value) || 0
      }));
      
      // Then recalculate totals
      setTimeout(() => recalculateTotals(), 0);
    } else {
      // For other fields, just update normally
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleQuoteSelect = (quoteId) => {
    const quote = quotes.find(q => q._id === quoteId);
    if (quote) {
      setSelectedQuote(quote);
      
      // Prefill form data with quote information
      const newItems = quote.items.map(item => ({
        device_type_id: item.deviceType._id,
        description: `${item.deviceType.name} - ${item.deviceType.modelName}`,
        quantity: item.quantity,
        unit_price: parseFloat(item.unit_price),
        total_price: parseFloat(item.total_price)
      }));

      setFormData(prev => ({
        ...prev,
        quote_id: quote._id,
        account_id: quote.account._id,
        invoice_number: `INV-${quote.quoteNumber}`,
        items: newItems
      }));
      
      // Recalculate totals after setting items
      setTimeout(() => {
        const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
        
        let cgstAmount = 0;
        let sgstAmount = 0;
        let igstAmount = 0;
        let totalTax = 0;

        if (formData.gst_type === 'intra') {
          cgstAmount = Math.round(subtotal * (formData.cgst_rate / 100));
          sgstAmount = Math.round(subtotal * (formData.sgst_rate / 100));
          totalTax = cgstAmount + sgstAmount;
        } else if (formData.gst_type === 'inter') {
          igstAmount = Math.round(subtotal * ((formData.igst_rate || 0) / 100));
          totalTax = igstAmount;
        }

        const total = subtotal + totalTax;

        setFormData(prev => ({
          ...prev,
          subtotal: subtotal,
          cgst_amount: cgstAmount,
          sgst_amount: sgstAmount,
          igst_amount: igstAmount,
          total_tax: totalTax,
          total: total
        }));
      }, 100);
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        device_type_id: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0
      }]
    }));
    // Recalculate totals after adding item
    setTimeout(() => recalculateTotals(), 0);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    recalculateTotals();
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-calculate total_price when quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            const quantity = field === 'quantity' ? parseFloat(value) : item.quantity;
            const unitPrice = field === 'unit_price' ? parseFloat(value) : item.unit_price;
            updatedItem.total_price = quantity * unitPrice;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));

    // Recalculate totals after item update
    setTimeout(() => recalculateTotals(), 0);
  };

  const recalculateTotals = () => {
    setFormData(prev => {
      const newItems = prev.items.map((item, i) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        return {
          ...item,
          total_price: quantity * unitPrice
        };
      });

      // Calculate subtotal from all items
      const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
      
      let cgstAmount = 0;
      let sgstAmount = 0;
      let igstAmount = 0;
      let totalTax = 0;

      // Calculate GST based on subtotal
      if (prev.gst_type === 'intra') {
        cgstAmount = Math.round(subtotal * (prev.cgst_rate / 100));
        sgstAmount = Math.round(subtotal * (prev.sgst_rate / 100));
        totalTax = cgstAmount + sgstAmount;
      } else if (prev.gst_type === 'inter') {
        igstAmount = Math.round(subtotal * ((prev.igst_rate || 0) / 100));
        totalTax = igstAmount;
      }

      // Total = Subtotal + GST
      const total = subtotal + totalTax;

      return {
        ...prev,
        items: newItems,
        subtotal: subtotal,
        cgst_amount: cgstAmount,
        sgst_amount: sgstAmount,
        igst_amount: igstAmount,
        total_tax: totalTax,
        total: total
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        items: formData.items.filter(item => item.device_type_id && item.quantity > 0)
      };

      // Try different API endpoints
      let response;
      let endpoint;

      if (formData.quote_id) {
        // Try with quote ID first
        endpoint = `/invoices/from-quote/${formData.quote_id}`;
        try {
          response = await post(endpoint, payload);
        } catch (firstError) {
          // Fallback to basic endpoint
          endpoint = '/invoices/from-quote';
          response = await post(endpoint, payload);
        }
      } else {
        // No quote ID, use basic endpoint
        endpoint = '/invoices/from-quote';
        response = await post(endpoint, payload);
      }
      
      // Handle different response structures
      if (response?.success) {
        toast.success('Invoice created successfully!');
        navigate('/admin/invoices');
      } else if (response?.data) {
        // Some APIs return data directly
        toast.success('Invoice created successfully!');
        navigate('/admin/invoices');
      } else if (response && typeof response === 'object') {
        // Direct success response
        toast.success('Invoice created successfully!');
        navigate('/admin/invoices');
      } else {
        throw new Error(response?.message || 'Failed to create invoice');
      }
    } catch (error) {
      
      
      let errorMessage = 'Failed to create invoice';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex text-foreground justify-between items-center">
        <h1 className="text-2xl font-bold">Create Invoice</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/admin/invoices')}
            className="flex items-center gap-2 border-1 border-muted-foreground/20"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
        {/* Main Form Section */}
        <div className="lg:col-span-2 space-y-6 ">
          {/* Basic Information Card */}
          <Card className="border-1 border-muted-foreground/20">
            <CardHeader>
              <CardTitle className="text-xl">Invoice Information</CardTitle>
              <CardDescription>Basic invoice details and customer information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 ">
                    <Label htmlFor="quote_id" className={labelClassName}>Quote Number</Label>
                    <Select value={formData.quote_id} onValueChange={handleQuoteSelect}>
                      <SelectTrigger className={selectTriggerClassName}>
                        <SelectValue placeholder="Select a quote" />
                      </SelectTrigger>
                      <SelectContent className='border-1 border-muted-foreground/20 '>
                        {getAvailableQuotes().length === 0 ? (
                          <SelectItem value="no-quotes" disabled>
                            No available quotes (all quotes already have invoices)
                          </SelectItem>
                        ) : (
                          getAvailableQuotes().map((quote) => (
                            <SelectItem key={quote._id} value={quote._id}>
                              {quote.quoteNumber} - {quote.account.name} (₹{quote.amount})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invoice_number" className={labelClassName}>Invoice Number *</Label>
                    <Input
                      id="invoice_number"
                      className={fieldClassName}
                      value={formData.invoice_number}
                      onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                      placeholder="INV-00001"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status" className={labelClassName}>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)} disabled>
                      <SelectTrigger className={selectTriggerClassName}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="issue_date">Issue Date</Label>
                    <div className="text-sm font-medium text-muted-foreground p-2 border-1 border-muted-foreground/20  rounded-md ">
                      {new Date(formData.issue_date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      }).toUpperCase()}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date</Label>
                    <div className="text-sm font-medium text-muted-foreground p-2 border-1 border-muted-foreground/20 rounded-md ">
                      {new Date(formData.due_date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      }).toUpperCase()}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="notes" className={labelClassName}>Notes</Label>
                  <Textarea
                    className="min-h-[100px] rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50"
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Payment terms and conditions..."
                    rows={3}
                  />
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Items Card */}
          <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className=''>
                  <CardTitle className="text-xl">Invoice Items</CardTitle>
                  <CardDescription>Add products or services to this invoice</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No items added yet. Click "Add Item" to get started.
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="border-1 border-muted-foreground/20 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Item {index + 1}</h4>
                       
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label className={labelClassName}>Description</Label>
                          <Input
                            className={fieldClassName}
                            disabled
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className={labelClassName}>Quantity *</Label>
                          <Input
                            className={fieldClassName}
                            type="number"
                            min="1"
                            disabled
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            placeholder="1"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className={labelClassName}>Unit Price *</Label>
                          <Input
                            type="number"
                            className={fieldClassName}
                            min="0"
                            step="0.01"
                            disabled
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className={labelClassName}>Total Amount</Label>
                          <Input
                            type="number"
                            className={fieldClassName}
                              value={item.total_price}
                            disabled
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <div className="text-sm font-medium">
                          Total: ₹{item.total_price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Section */}
        <div className="lg:col-span-1 space-y-4 ">
          {/* Invoice Information Summary */}
          <div className="p-6 rounded-lg shadow-sm text-foreground border-1 border-muted-foreground/20 bg-card">
            <h3 className="text-lg font-semibold mb-4">Invoice Information</h3>
            
            <div className="space-y-3">
              

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Invoice Number:</span>
                <span className="text-sm font-medium text-muted-foreground">
                  {formData.invoice_number || "Will be generated"}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Status:</span>
                <span className="text-sm font-medium capitalize text-muted-foreground">{formData.status}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Issue Date:</span>
                <span className="text-sm font-medium text-muted-foreground">{formData.issue_date}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Due Date:</span>
                <span className="text-sm font-mediu text-muted-foreground">{formData.due_date}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Notes:</span>
                <span className="text-sm font-medium max-w-32 truncate text-muted-foreground" title={formData.notes}>
                  {formData.notes || "No notes"}
                </span>
              </div>
            </div>
          </div>

          {/* Items Summary */}
          <div className="p-6 rounded-lg shadow-sm border-1 border-muted-foreground/20 bg-card">
            <h3 className="text-lg text-foreground mb-4">Items Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-foreground">Total Items:</span>
                <span className="text-sm font-medium text-muted-foreground">{formData.items.length} items</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Subtotal:</span>
                <span className="text-sm font-medium text-muted-foreground">₹{formData.subtotal.toFixed(2)}</span>
              </div>

              {formData.items.length > 0 && (
                <div className="mt-4 space-y-2 ">
                  <h4 className="text-sm font-medium text-foreground">Item Details:</h4>
                  {formData.items.map((item, index) => (
                    <div key={index} className="p-2 rounded border-1 border-muted-foreground/20">
                      <div className="text-xs text-foreground">Item {index + 1}</div>
                      <div className="text-sm font-medium text-muted-foreground my-2">{item.description || "No description"}</div>
                      <div className="text-xs text-muted-foreground my-2 ">
                        Qty: {item.quantity} × ₹{item.unit_price.toFixed(2)} = ₹{item.total_price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* GST Configuration */}
          <div className="p-6 rounded-lg shadow-sm border-1 border-muted-foreground/20 bg-card">
            <h3 className="text-lg text-foreground mb-4">GST Configuration</h3>
            
            <div className="space-y-4">
              {/* GST Type Selection */}
              <div className="space-y-2">
                <Label className={labelClassName}>GST Type</Label>
                <Select value={formData.gst_type} onValueChange={(value) => handleInputChange('gst_type', value)}>
                  <SelectTrigger className={`w-full ${selectTriggerClassName}`}>
                    <SelectValue placeholder="Select GST type" />
                  </SelectTrigger>
                  <SelectContent className='border-1 border-muted-foreground/20'>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="intra">Intra (CGST+SGST)</SelectItem>
                    <SelectItem value="inter">Inter (IGST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* GST Rate Inputs */}
              {formData.gst_type === 'intra' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground">CGST %</Label>
                    <Input 
                      value={formData.cgst_rate}
                      onChange={(e) => handleInputChange('cgst_rate', e.target.value)}
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0"
                      className={fieldClassName}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground">SGST %</Label>
                    <Input 
                      value={formData.sgst_rate}
                      onChange={(e) => handleInputChange('sgst_rate', e.target.value)}
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0"
                      className={fieldClassName}
                    />
                  </div>
                </div>
              )}

              {formData.gst_type === 'inter' && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-foreground">IGST %</Label>
                  <Input 
                    value={formData.igst_rate || ''}
                    onChange={(e) => handleInputChange('igst_rate', e.target.value)}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    className={fieldClassName}
                  />
                </div>
              )}

              {/* GST Amounts Display */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">GST Amounts</h4>
                <div className="space-y-2">
                  {formData.gst_type === 'intra' && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">CGST ({formData.cgst_rate}%):</span>
                        <span className="font-medium text-muted-foreground">₹{formData.cgst_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">SGST ({formData.sgst_rate}%):</span>
                        <span className="font-medium text-muted-foreground">₹{formData.sgst_amount.toFixed(2)}</span>
                      </div>
                    </>
                  )}

                  {formData.gst_type === 'inter' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">IGST ({formData.igst_rate || 0}%):</span>
                      <span className="font-medium">₹{(formData.igst_amount || 0).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm font-semibold border-t pt-2">
                    <span className="text-foreground">Total Tax:</span>
                    <span className="text-foreground">₹{formData.total_tax.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final Summary */}
          <div className="p-6 rounded-lg shadow-sm border-1 border-muted-foreground/20 bg-card">
            <h3 className="text-lg text-foreground mb-4">Final Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-foreground">Subtotal:</span>
                <span className="text-sm font-medium text-muted-foreground">₹{formData.subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-foreground">Tax Amount:</span>
                <span className="text-sm font-medium text-muted-foreground">₹{formData.total_tax.toFixed(2)}</span>
              </div>

              <div className=" pt-3">
                <div className="flex justify-between">
                  <span className="text-base font-semibold text-foreground">Total Amount:</span>
                  <span className="text-base text-muted-foreground">₹{formData.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Generate Invoice Button */}
          <div className="p-6 rounded-lg shadow-sm border-1 border-muted-foreground/20 ">
            <Button 
              onClick={handleSubmit} 
              variant='outline'
              className="w-full text-foreground border-1 border-muted-foreground/20"
              disabled={loading}
              size="sm"
            >
              {loading ? (
                <>
                  <div className="outline"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 text-foreground " />
                  Create Invoice
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}